<template>
    <van-overlay :show="showInsufficientPhysicalStrengthDialog" z-index="2000" @click="handleOverlayClick">
        <div class="insufficient-dialog-container" @touchmove.stop>

            <!-- 关闭按钮 -->
            <div class="close-btn" @click="handleClose">
                <img src="@/assets/images/message/insufficientPhysicalStrengthDialog/close.svg" alt=""
                    class="w-24px h-24px" />
            </div>

            <!-- 图标区域 -->
            <div class="icon-wrapper">
                <img src="@/assets/images/message/tili.svg" alt="" class="w-40px h-40px" />
            </div>

            <div class="color-#01031B text-24px font-800">
                <span class="current">{{ userStore.getStrengthBalance() }}</span><span class="all">/{{
                    userStore.equipmentInformation?.config?.strengthMax }}</span>
            </div>
            <div class="countdown color-#01031B"
                v-if="userStore.equipmentInformation?.config?.strengthPrice < userStore.equipmentInformation?.config?.strengthMax">
                {{ countdown }}</div>

            <!-- 标题 -->
            <div class="dialog-title color-#01031B! text-16px mt-20px">
                {{ t('dialog.insufficientEnergy') }}
            </div>

            <!-- 描述文字 -->
            <div class="dialog-description color-#000!">
                {{ t('dialog.insufficientEnergyDesc') }}
            </div>


            <!-- 操作按钮 -->
            <div class="dialog-actions ">
                <div class="btn-primary flex items-center justify-between" @click="handleConfirm">
                    <div class="text-16px color-#fff flex items-center justify-center">
                        <img src="@/assets/images/message/tili.svg" alt="" class="w-16px h-16px" />
                        <div class="ml-4px">
                            +5
                        </div>
                    </div>
                    <div class="text-10px consumes">
                        {{ t('dialog.consumesGems', { gems: userStore.getPhysicalStrength() * userStore.equipmentInformation?.config?.strengthMax }) }}
                    </div>
                </div>
            </div>
        </div>
    </van-overlay>
</template>

<script lang="ts" setup>
import { logEventSafe } from '@/plugins/firebase'
import { MessageAPI } from '@/services/message'
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useUserStore } from '@/stores/user'
import { useInsufficientPhysicalStrengthDialog } from '@/composables/useInsufficientPhysicalStrengthDialog'
import { showSuccessToast, showErrorToast, showLoadingToast, closeToast } from '@/utils/toast'
import { useI18n } from 'vue-i18n'
const userStore = useUserStore()
const { showInsufficientPhysicalStrengthDialog, closeInsufficientPhysicalStrengthDialog } = useInsufficientPhysicalStrengthDialog()
const { t } = useI18n()

// 体力值
const currentStrength = computed(() => userStore.equipmentInformation?.config?.strengthPrice || 0)
const maxStrength = computed(() => userStore.equipmentInformation?.config?.strengthMax || 100)
const requiredStrength = computed(() => 10) // 默认需要 10 体力

// 体力百分比
const strengthPercent = computed(() => {
    const max = maxStrength.value
    const current = currentStrength.value
    if (max <= 0) return 0
    return Math.min(100, (current / max) * 100)
})

// 按钮文字
const confirmText = computed(() => t('dialog.getStrength'))
const cancelText = computed(() => t('dialog.cancel'))
const showCancel = true
const countdown = ref('00:00')
let countdownTimer: any = null

// 关闭弹窗
const handleClose = () => {
    logEventSafe('free_energy_popup_close')
    closeInsufficientPhysicalStrengthDialog()
}

// 点击遮罩关闭
const handleOverlayClick = () => {
    if (showCancel) {
        handleClose()
    }
}

// 确认操作 - 打开商店
const handleConfirm = async () => {
    logEventSafe('free_energy_popup_button1_click')
    try {
        showLoadingToast()
        await MessageAPI.getBuyStrength({ strength: 5 })
        logEventSafe('free_energy_popup_5energy_buy_success')
        showSuccessToast(t('success.thankYouPurchase'))
        closeInsufficientPhysicalStrengthDialog()
        closeToast()
    } catch (error) {
        logEventSafe('free_energy_popup_5energy_buy_failed')
        closeToast()
    }
}

/**
 * 计算距离下一个整点的时间
 */
const calculateCountdown = () => {
    const now = new Date()
    const nextHour = new Date(now)
    nextHour.setHours(now.getHours() + 1, 0, 0, 0) // 下一个整点

    const diff = nextHour.getTime() - now.getTime()

    const hours = Math.floor(diff / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((diff % (1000 * 60)) / 1000)

    countdown.value = `00:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

/**
 * 组件挂载时启动倒计时
 */
onMounted(() => {
    calculateCountdown()
    countdownTimer = setInterval(calculateCountdown, 1000)
})

/**
 * 组件卸载时清理定时器
 */
onUnmounted(() => {
    if (countdownTimer) {
        clearInterval(countdownTimer)
        countdownTimer = null
    }
})
</script>

<style scoped lang="scss">
.van-overlay {
    background: rgba(0, 0, 0, 0.6) !important;
    backdrop-filter: blur(4px);
}

.insufficient-dialog-container {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 85%;
    max-width: 320px;
    background: linear-gradient(180deg, #1a1c2e 0%, #0f1221 100%);
    border-radius: 20px;
    // border: 1px solid rgba(255, 255, 255, 0.1);
    background: #FFFFFF;
    padding: 30px 24px 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
}

.close-btn {
    position: absolute;
    top: 12px;
    right: 12px;
    cursor: pointer;
    opacity: 0.7;
    transition: opacity 0.2s;

    &:hover {
        opacity: 1;
    }
}

.icon-wrapper {
    margin-bottom: 16px;
}

.dialog-title {
    font-size: 18px;
    font-weight: 600;
    color: #ffffff;
    margin-bottom: 6px;
}

.dialog-description {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.6);
    margin-bottom: 24px;
    line-height: 1.5;
}

.strength-display {
    width: 100%;
    margin-bottom: 24px;
}

.current-strength {
    margin-bottom: 8px;
}

.strength-value {
    font-size: 24px;
    font-weight: 700;
    color: #ff6b6b;
}

.strength-unit {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.5);
}

.strength-bar {
    width: 100%;
    height: 8px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    overflow: hidden;
}

.strength-progress {
    height: 100%;
    background: linear-gradient(90deg, #ff6b6b 0%, #ffa502 100%);
    border-radius: 4px;
    transition: width 0.3s ease;
}

.dialog-actions {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.btn-primary {
    width: 100%;
    padding: 10px 14px;
    // background: linear-gradient(90deg, #5A68FF 0%, #95A5FF 100%);
    background-color: #01031B;
    border-radius: 22px;
    font-weight: 600;
    font-size: 16px;
    color: #ffffff;
    cursor: pointer;
    transition: opacity 0.2s;

    &:active {
        opacity: 0.8;
    }
}

.btn-secondary {
    width: 100%;
    padding: 14px;
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 23px;
    font-weight: 500;
    font-size: 16px;
    color: rgba(255, 255, 255, 0.7);
    cursor: pointer;
    transition: all 0.2s;

    &:active {
        background: rgba(255, 255, 255, 0.1);
    }
}

.all {
    color: rgba(1, 3, 27, 0.30);
}

.countdown {
    color: rgba(1, 3, 27, 0.30);
}

.dialog-description {
    font-size: 14px;
    color: #01031B;
    font-weight: 500;
}

.consumes {
    color: rgba(255, 255, 255, 0.6);
}
</style>
